<template>
  <div
    v-if="enabled"
    class="upsell-products"
  >
    <div
      class="upsell-products__product-list"
    >
      <p
        v-if="title"
        class="upsell-products__title text-heading-l"
        v-text="title"
      />
      <splide
        class="upsell-products__carousel"
        :has-track="false"
        :options="localConfig"
        :aria-label="$string('cart.product_upsell.accessibility')"
      >
        <div
          class="upsell-products__arrows splide__arrows"
          :class="type"
        >
          <button
            class="splide__arrow splide__arrow--prev"
            type="button"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.previous')"
            />

            <icon-arrow-left />
          </button>

          <button
            class="splide__arrow splide__arrow--next"
            type="button"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.next')"
            />

            <icon-arrow-right />
          </button>
        </div>
        <splide-track>
          <template v-for="(product, index) of products">
            <splide-slide
              v-if="product.available"
              :key="`upsell-products-cart-${product.handle}-${index}`"
            >
              <div
                v-if="product.available"
                class="upsell-products__product-card"
                :class="{
                  [$class('loading')]: !product,
                  'show-secondary-image': showSecondaryImage,
                }"
                role="button"
                tabIndex="0"
                @click="setQuickViewHandle(product.handle)"
              >
                <div class="upsell-products__product-card-image-container">
                  <responsive-image
                    class="upsell-products__product-card-image"
                    :image="product.featuredImage"
                    :lazyload="lazyload"
                    ratio="4:5"
                    sizes="(min-width: 1024px) 25vw, 50vw"
                  />
                </div>

                <div class="upsell-products__product-card-content">
                  <p
                    class="
                    upsell-products__product-card-title
                    text-body-s-bold
                  "
                    v-text="product.title"
                  />

                  <product-prices
                    class="product-card__prices"
                    :available="product?.available"
                    :compare-at-price="product?.compareAtPrice?.amount"
                    :loading="!product"
                    :price="product?.price?.amount"
                    :varies="product?.price?.varies"
                    :price-first="false"
                  />

                  <p
                    v-if="product?.availableOptions"
                    class="text-body-xs-regular product-card__available-options"
                    v-text="`${$string('product.available_in')} ${product?.availableOptions}`"
                  />
                </div>
              </div>
            </splide-slide>
          </template>
        </splide-track>
      </splide>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Upsell Products cart drawer (upsell-products)
 * -----------------------------------------------------------------------------
 * Display upsell products in cart drawer.
 * - Component settings: snippet / dynamic / async / scroll
 *
 */
import { mapActions, mapState } from 'vuex'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import ResponsiveImage from '~global/images/responsive-image'

import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg'
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'
import collectionQuery from '~graphql/collection.gql'
import { formatCollection } from '~/format/format-collection'
import ProductPrices from '~global/product-prices/product-prices'

export default {
  name: 'UpsellProducts',

  components: {
    IconArrowLeft,
    IconArrowRight,
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
    ProductPrices,
  },

  data() {
    return {
      enabled: window.cnvs.cart.upsell.enable,
      title: window.cnvs.cart.upsell.title,
      thresholdMet: false,
      products: [],
      state: '',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute local config for splide carousel.
     * @returns {Object}
     */
    localConfig() {
      const localConfig = {
        arrows: true,
        direction: 'ltr',
        drag: true,
        flickPower: 200,
        flickMaxPages: 1,
        gap: '0.75rem',
        mediaQuery: 'min',
        speed: this.$timing('normal'),
        start: this.currentIndex || 0,
        type: 'slide',
        pagination: false,
        padding: {
          right: this.isMobile ? '25%' : '36%',
          left: '0',
        },
      }

      return localConfig
    },
  },

  mounted() {
    const handle = window.cnvs.cart.upsell.collection
    this.queryProducts(handle)
  },

  methods: {

    /**
     * Query collection and format response.
     * @param {String} handle - Collection handle to load.
     * @param {Number} first - How many products to load.
     * @returns {Array}
     */
    async queryProducts(handle, first = 4) {
      try {
        const response = await cnvs.Query({
          query: collectionQuery,
          variables: {
            country: cnvs.store.country,
            first,
            handle,
            language: cnvs.store.language,
          },
        })

        const collectionFormatted = formatCollection(response)
        this.products = collectionFormatted.products.map((product) => ({
          ...product,
          availableOptions: product.variants?.length > 0 &&
            product.variants.filter(variant => variant.available).map(variant => variant.title).join(', '),
        }))

        console.log(this.products)

      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },

    ...mapActions({
      setQuickViewHandle: 'quick-view/setHandle',
    }),
  },
}
</script>

<style lang="scss">
@import './upsell-products';
@import '@/components/splide';
</style>
